import { useState } from 'react';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import {
  useRorosActorDeactivate,
  useRorosActorReadinessAssessments,
} from '@/hooks/roros/actors';
import type { RorosActorDTO } from '@/types/roros/actors';

interface UseActorLifecycleParams {
  actor?: RorosActorDTO | null;
  isEdit: boolean;
  isAiOp: boolean;
  onClose: () => void;
}

/**
 * Owns the actor "lifecycle" concern of UnifiedActorEditor: readiness
 * assessments + promote-dialog visibility, and the deactivate confirm/mutation
 * flow. Behaviour-identical extraction (M10f decomposition).
 */
export function useActorLifecycle({ actor, isEdit, isAiOp, onClose }: UseActorLifecycleParams) {
  const deactivateMutation = useRorosActorDeactivate();

  const assessmentsQuery = useRorosActorReadinessAssessments(
    isEdit && isAiOp ? (actor?.id ?? '') : '',
  );
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  const handleDeactivate = () => {
    if (!actor?.id) return;
    deactivateMutation.mutate(actor.id, {
      onSuccess: () => {
        showToast.success(t('Actor deactivated'));
        setShowDeactivateConfirm(false);
        onClose();
      },
      onError: (err) => {
        showToast.error(err instanceof Error ? err.message : t('Failed to deactivate'));
        setShowDeactivateConfirm(false);
      },
    });
  };

  return {
    deactivateMutation,
    assessmentsQuery,
    showPromoteDialog,
    setShowPromoteDialog,
    showDeactivateConfirm,
    setShowDeactivateConfirm,
    handleDeactivate,
  };
}
